/**
 * MIF Import Wizard (TRUS-149 / TRUS-366)
 *
 * Loads a health plan's monthly Member Information File without overwriting data
 * the organization's own staff have entered.
 *
 * Seven steps. Three of them are the reason this exists rather than reusing the
 * generic CSV wizard:
 *
 *   Verify   the plan quietly renaming or dropping a column is how a recurring
 *            import starts discarding a field with nobody noticing
 *   Review   what the load would change, per member and per field, with the
 *            protected fields shown rather than hidden
 *   Resolve  the ambiguous matches and unreadable values a load must not guess at
 *
 * The protection is deliberately visible. An operator who cannot see that the
 * plan's stale copy of their ECM status was refused has no way to trust the load.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Copy,
  FileSpreadsheet,
  GitCompare,
  History,
  Layers,
  Loader2,
  Lock,
  RotateCcw,
  ShieldCheck,
  Upload,
  UserPlus,
  Users,
  X,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { SearchableSelect } from '../ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

import { useAuth } from '@/contexts/AuthContext';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { organizationsAPI } from '@/services/api/organizations';
import { programsAPI, mifPlanKeyForProgram } from '@/services/api/programs';
import type { Program } from '@/services/api/programs';
import {
  ALL_PLANS,
  DEFAULT_STATE,
  MifLayoutCatalogControls,
  mineOnly,
  plansIn,
  statesFrom,
} from './MifLayoutCatalogControls';
import type { LayoutView } from './MifLayoutCatalogControls';

import {
  ACTION_LABELS,
  POLICY_LABELS,
  acknowledgeMissingColumns,
  approvePack,
  commitBatch,
  getPackRules,
  getTargetFields,
  deriveLayout,
  installPack,
  listBatches,
  listLayouts,
  listChurn,
  listGaps,
  listPacks,
  listProposals,
  listRows,
  planBatch,
  clonePack,
  clonePreview,
  decideDrift,
  decideProposal,
  discardBatch,
  getDrift,
  getPackHistory,
  previewRollback,
  provisionFields,
  resolveGap,
  revisePack,
  rollbackBatch,
  searchMembers,
  setLayoutStatus,
  updatePackRule,
  uploadMif,
} from '@/services/api/mifImport';
import type {
  AgentProposal,
  CellDecision,
  ClonePreview,
  FieldGap,
  ChurnItem,
  ContractDiff,
  DriftSummary,
  LayoutDrift,
  MifBatch,
  MifFieldRule,
  MifGap,
  MifLayout,
  MifPack,
  RuleChange,
  MifRow,
  PlanSummary,
  RollbackPreview,
  TargetFieldOption,
  TargetFieldOptions,
  UploadResult,
  WritePolicy,
} from '@/services/api/mifImport';

type Step = 'upload' | 'verify' | 'review' | 'resolve' | 'done';

const STEPS: { key: Step; label: string }[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'verify', label: 'Verify file' },
  { key: 'review', label: 'Review impact' },
  { key: 'resolve', label: 'Resolve gaps' },
  { key: 'done', label: 'Results' },
];

const errorText = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return error?.message || fallback;
};

const PROTECTED_ACTIONS = new Set([
  'skip_org_owned',
  'skip_human_edited',
  'skip_frozen',
  'skip_fill_occupied',
]);

// ===========================================================================

export const MifImportWizard: React.FC = () => {
  const [step, setStep] = useState<Step>('upload');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { t } = useAppTranslation();
  const [packs, setPacks] = useState<MifPack[]>([]);
  // TRUS-725: the plan comes from the program's health plan, chosen once in program
  // setup. There is no default — this used to be a hard-coded 'sfhp', which put SFHP
  // in front of orgs that never work with SFHP. With no program/plan, the user chooses.
  const [planKey, setPlanKey] = useState('');
  const [planPrograms, setPlanPrograms] = useState<Program[]>([]);
  const [programId, setProgramId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    programsAPI
      .listPrograms({ is_active: true })
      .then((list) => {
        if (cancelled) return;
        const withPlan = (list || []).filter((p) => mifPlanKeyForProgram(p));
        setPlanPrograms(withPlan);
        // Exactly one program with a plan: nothing to choose.
        if (withPlan.length === 1) {
          setProgramId(withPlan[0].id);
          setPlanKey((current) => current || mifPlanKeyForProgram(withPlan[0]) || '');
        }
      })
      .catch(() => {
        /* no programs loaded: the user picks a layout under Layouts instead */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const chooseProgram = (id: number | null) => {
    setProgramId(id);
    const program = planPrograms.find((p) => p.id === id);
    setPlanKey(mifPlanKeyForProgram(program) || '');
  };

  const [upload, setUpload] = useState<UploadResult | null>(null);
  const [summary, setSummary] = useState<PlanSummary | null>(null);
  const [rows, setRows] = useState<MifRow[]>([]);
  const [gaps, setGaps] = useState<MifGap[]>([]);
  const [committed, setCommitted] = useState<any>(null);
  const [churn, setChurn] = useState<ChurnItem[]>([]);
  const [history, setHistory] = useState<MifBatch[]>([]);
  const [rollback, setRollback] = useState<RollbackPreview | null>(null);

  const approvedPack = useMemo(
    () => packs.find((p) => p.plan_key === planKey && p.status === 'approved'),
    [packs, planKey]
  );
  const draftPack = useMemo(
    () => packs.find((p) => p.plan_key === planKey && p.status === 'draft'),
    [packs, planKey]
  );

  const refreshPacks = useCallback(async () => {
    try {
      setPacks((await listPacks()).packs);
    } catch (e) {
      setError(errorText(e, 'Could not load the rule packs'));
    }
  }, []);

  const refreshHistory = useCallback(async () => {
    try {
      setHistory((await listBatches()).batches);
    } catch (e) {
      /* history is supplementary; a failure here must not block a load */
    }
  }, []);

  useEffect(() => {
    refreshPacks();
    refreshHistory();
  }, [refreshPacks, refreshHistory]);

  // -- step 1 ---------------------------------------------------------------

  const onDrop = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      setError(null);
      setBusy('Reading the file');
      try {
        const result = await uploadMif(files[0], planKey);
        setUpload(result);
        setStep('verify');
      } catch (e) {
        setError(errorText(e, 'Could not read the file'));
      } finally {
        setBusy(null);
      }
    },
    [planKey]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    disabled: !approvedPack || !!busy,
  });

  // -- steps 2-4 ------------------------------------------------------------

  const runPlan = async () => {
    if (!upload) return;
    setError(null);
    setBusy('Working out what this would change');
    try {
      // TRUS-656: a file missing some mapped columns is loaded only after the
      // reviewer has agreed those fields stay as they are; the server records it.
      if (upload.contract.severity === 'acknowledge' && !upload.contract.acknowledged_at) {
        const ack = await acknowledgeMissingColumns(upload.batch_id);
        setUpload({ ...upload, contract: { ...upload.contract, acknowledged_at: ack.acknowledged_at } });
      }
      const planned = await planBatch(upload.batch_id);
      setSummary(planned.summary);
      const [rowResult, gapResult] = await Promise.all([
        listRows(upload.batch_id, { limit: 200 }),
        listGaps(upload.batch_id),
      ]);
      setRows(rowResult.rows);
      setGaps(gapResult.gaps);
      setStep('review');
    } catch (e) {
      setError(errorText(e, 'Could not work out the impact of this file'));
    } finally {
      setBusy(null);
    }
  };

  const doCommit = async () => {
    if (!upload) return;
    setError(null);
    setBusy('Loading the file');
    try {
      const result = await commitBatch(upload.batch_id);
      setCommitted(result);
      setChurn((await listChurn(upload.batch_id)).churn);
      setStep('done');
      refreshHistory();
    } catch (e) {
      setError(errorText(e, 'The load failed and nothing was changed'));
    } finally {
      setBusy(null);
    }
  };

  // Leaving the file check abandons that upload, so it can't be picked up later
  // by mistake and a corrected file can be loaded in its place.
  const discardAndReset = async () => {
    if (upload) {
      try {
        await discardBatch(upload.batch_id);
      } catch {
        // Nothing to undo: an upload that can't be discarded was never loaded.
      }
    }
    reset();
  };

  const reset = () => {
    setUpload(null);
    setSummary(null);
    setRows([]);
    setGaps([]);
    setCommitted(null);
    setChurn([]);
    setRollback(null);
    setError(null);
    setStep('upload');
  };

  // -- pack setup -----------------------------------------------------------

  const doInstall = async () => {
    if (!planKey) return;
    setBusy('Matching the plan layout to your fields');
    setError(null);
    try {
      await installPack(planKey);
      await refreshPacks();
    } catch (e) {
      setError(errorText(e, 'Could not build the rule pack'));
    } finally {
      setBusy(null);
    }
  };

  // =========================================================================

  return (
    <div className="space-y-6">
      <StepBar current={step} />

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {busy && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {busy}…
        </div>
      )}

      {step === 'upload' && (
        <div className="space-y-1">
          <label htmlFor="mif-program" className="text-sm font-medium">
            {t('mifImport.programLabel', 'Program')}
          </label>
          <select
            id="mif-program"
            value={programId ?? ''}
            onChange={(e) => chooseProgram(e.target.value ? Number(e.target.value) : null)}
            className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
          >
            <option value="">{t('mifImport.chooseProgram', 'Choose the program this file is for')}</option>
            {planPrograms.map((p) => (
              <option key={p.id} value={p.id}>
                {p.payer_name ? `${p.name} · ${p.payer_name}` : p.name}
              </option>
            ))}
          </select>
          {planPrograms.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t(
                'mifImport.noProgramPlan',
                'No program has a health plan yet. Choose one in Program setup, or pick a layout under Layouts.'
              )}
            </p>
          )}
        </div>
      )}

      {step === 'upload' && (
        <UploadStep
          planKey={planKey}
          onPlanKeyChange={setPlanKey}
          approvedPack={approvedPack}
          draftPack={draftPack}
          onInstall={doInstall}
          onPackChanged={refreshPacks}
          dropzone={{ getRootProps, getInputProps, isDragActive }}
          history={history}
          onRolledBack={refreshHistory}
        />
      )}

      {step === 'verify' && upload && (
        <VerifyStep upload={upload} onBack={discardAndReset} onContinue={runPlan} busy={!!busy} />
      )}

      {step === 'review' && summary && (
        <ReviewStep
          summary={summary}
          rows={rows}
          gapCount={gaps.length}
          onBack={() => setStep('verify')}
          onResolve={() => setStep('resolve')}
          onCommit={doCommit}
          busy={!!busy}
        />
      )}

      {step === 'resolve' && upload && (
        <ResolveStep
          batchId={upload.batch_id}
          gaps={gaps}
          onBack={() => setStep('review')}
          onReplan={runPlan}
        />
      )}

      {step === 'done' && committed && (
        <ResultsStep
          result={committed}
          churn={churn}
          batchId={upload!.batch_id}
          rollbackPreview={rollback}
          setRollbackPreview={setRollback}
          onDone={reset}
          onRolledBack={() => {
            refreshHistory();
            reset();
          }}
        />
      )}
    </div>
  );
};

// ===========================================================================
// Step bar
// ===========================================================================

const StepBar: React.FC<{ current: Step }> = ({ current }) => {
  const index = STEPS.findIndex((s) => s.key === current);
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <div
              className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                i < index
                  ? 'bg-primary text-primary-foreground'
                  : i === index
                    ? 'border-2 border-primary text-primary'
                    : 'border border-muted-foreground/30 text-muted-foreground'
              }`}
            >
              {i < index ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span className={`text-sm ${i === index ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40" />}
        </React.Fragment>
      ))}
    </div>
  );
};

// ===========================================================================
// Step 1 — upload, with pack setup and the history log
// ===========================================================================

const UploadStep: React.FC<{
  planKey: string;
  onPlanKeyChange: (k: string) => void;
  approvedPack?: MifPack;
  draftPack?: MifPack;
  onInstall: () => void;
  onPackChanged: () => void;
  dropzone: any;
  history: MifBatch[];
  onRolledBack: () => void;
}> = ({ planKey, onPlanKeyChange, approvedPack, draftPack, onInstall, dropzone, history, onPackChanged }) => {
  // A pack has to be reviewed before it can be approved, and approving is what
  // unlocks the dropzone — so an unapproved pack opens on the rules tab rather
  // than on a dropzone that cannot accept anything.
  const [tab, setTab] = useState(approvedPack ? 'load' : 'rules');
  // Copying between organizations is platform-staff work. The backend refuses
  // anyone else; this only avoids showing a tab that cannot be used.
  const { user } = useAuth();
  const isPlatformStaff = user?.role === 'super_admin' || user?.role === 'ops_admin';
  const { getRootProps, getInputProps, isDragActive } = dropzone;
  const { t } = useAppTranslation();

  return (
    <Tabs value={tab} onValueChange={setTab} className="w-full">
      <TabsList>
        <TabsTrigger value="load" className="flex items-center gap-2">
          <Upload className="h-4 w-4" /> Load a file
        </TabsTrigger>
        <TabsTrigger value="layouts" className="flex items-center gap-2">
          <Layers className="h-4 w-4" /> Layouts
        </TabsTrigger>
        <TabsTrigger value="rules" className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> Field rules
        </TabsTrigger>
        {isPlatformStaff && (
          <TabsTrigger value="reuse" className="flex items-center gap-2">
            <Copy className="h-4 w-4" /> Reuse elsewhere
          </TabsTrigger>
        )}
        <TabsTrigger value="changes" className="flex items-center gap-2">
          <GitCompare className="h-4 w-4" /> Changes
        </TabsTrigger>
        <TabsTrigger value="history" className="flex items-center gap-2">
          <History className="h-4 w-4" /> Past loads
          {history.length > 0 && <Badge variant="secondary">{history.length}</Badge>}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="load" className="pt-4">
        {!planKey ? (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('mifImport.choosePlanTitle', 'Choose the health plan first')}</AlertTitle>
            <AlertDescription>
              {t(
                'mifImport.choosePlanBody',
                "Pick the program this file is for — its health plan decides which layout and rules apply. If the plan isn't on a program yet, choose a layout under Layouts."
              )}
            </AlertDescription>
          </Alert>
        ) : !approvedPack ? (
          <Alert>
            <ShieldCheck className="h-4 w-4" />
            <AlertTitle>Set up the field rules first</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>
                A load needs an approved set of rules saying which of the plan's columns may update
                which of your fields — and which must never overwrite what your staff have entered.
              </p>
              <div className="flex gap-2">
                {draftPack ? (
                  <Button size="sm" onClick={() => setTab('rules')}>
                    Review the draft rules
                  </Button>
                ) : (
                  <Button size="sm" onClick={onInstall}>
                    Build the rules for {planKey.toUpperCase()}
                  </Button>
                )}
              </div>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="secondary">{approvedPack.plan_name}</Badge>
              <span>layout {approvedPack.layout_version}</span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Lock className="h-3.5 w-3.5" />
                {approvedPack.protected_count} protected field
                {approvedPack.protected_count === 1 ? '' : 's'}
              </span>
              {approvedPack.unmapped_count > 0 && (
                <>
                  <span>·</span>
                  <span className="text-amber-600 dark:text-amber-500">
                    {approvedPack.unmapped_count} column{approvedPack.unmapped_count === 1 ? '' : 's'} still
                    need a field
                  </span>
                </>
              )}
            </div>

            <div
              {...getRootProps()}
              className={`cursor-pointer rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
                isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
            >
              <input {...getInputProps()} />
              <FileSpreadsheet className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-3 font-medium">Drop this month's file here, or click to choose one</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Excel or CSV. The reporting period and production date are read from the file.
              </p>
            </div>
          </div>
        )}
      </TabsContent>

      <TabsContent value="layouts" className="pt-4">
        <LayoutsPanel
          planKey={planKey}
          onPlanKeyChange={onPlanKeyChange}
          onPackInstalled={onPackChanged}
        />
      </TabsContent>

      <TabsContent value="rules" className="pt-4">
        <PackRules
          packId={approvedPack?.id ?? draftPack?.id}
          onPackChanged={onPackChanged}
          onApproved={() => {
            onPackChanged();
            setTab('load');
          }}
        />
      </TabsContent>

      {isPlatformStaff && (
        <TabsContent value="reuse" className="pt-4">
          <ClonePanel packId={approvedPack?.id ?? draftPack?.id} packStatus={approvedPack ? 'approved' : 'draft'} />
        </TabsContent>
      )}

      <TabsContent value="changes" className="pt-4">
        <ChangesPanel packId={approvedPack?.id ?? draftPack?.id} planKey={planKey} />
      </TabsContent>

      <TabsContent value="history" className="pt-4">
        <HistoryTable history={history} />
      </TabsContent>
    </Tabs>
  );
};

// ===========================================================================
// Field rules (step 3 of the seven — the mapping and policy review)
// ===========================================================================

/**
 * Step 3 — the mapping and policy review, and the only place a pack becomes
 * approvable.
 *
 * This is an editing surface, not a report. A pack arrives from install with some
 * columns unresolved — the plan sends a field this organization has never modelled
 * — and those are decisions someone owes, so they have to be answerable here.
 *
 * Unmapped columns are shown first and by default. Everything else is already
 * decided; the work is the exceptions.
 */
const PackRules: React.FC<{
  packId?: number;
  onApproved?: () => void;
  onPackChanged?: () => void;
}> = ({ packId, onApproved, onPackChanged }) => {
  const [data, setData] = useState<{
    pack: {
      id: number;
      status: string;
      plan_name: string;
      plan_key: string;
      layout_version: string;
      committed_loads: number;
      can_reopen: boolean;
      approved_by_name: string | null;
      approved_at: string | null;
    };
    rules: MifFieldRule[];
    counts: Record<string, number>;
  } | null>(null);
  const [targets, setTargets] = useState<TargetFieldOptions | null>(null);
  const [filter, setFilter] = useState<'unmapped' | 'protected' | 'all'>('unmapped');
  const [saving, setSaving] = useState<string | null>(null);
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [revising, setRevising] = useState(false);

  const load = useCallback(async () => {
    if (!packId) return;
    try {
      const [rules, fields] = await Promise.all([getPackRules(packId), getTargetFields(packId)]);
      setData(rules);
      setTargets(fields);
    } catch (e) {
      setProblem(errorText(e, 'Could not load the field rules'));
    }
  }, [packId]);

  useEffect(() => {
    load();
  }, [load]);

  const editable = data?.pack.status === 'draft';

  const save = async (column: string, changes: Partial<MifFieldRule>) => {
    if (!packId) return;
    setSaving(column);
    setProblem(null);
    try {
      await updatePackRule(packId, column, changes);
      // Re-read rather than patching local state: the server may have adjusted
      // more than was sent — choosing a field moves the policy off "needs a field",
      // and clearing one moves it back.
      await load();
      setSaved((prev) => ({ ...prev, [column]: true }));
      setTimeout(() => setSaved((prev) => ({ ...prev, [column]: false })), 2500);
    } catch (e) {
      setProblem(errorText(e, `Could not update ${column}`));
    } finally {
      setSaving(null);
    }
  };

  const doRevise = async () => {
    if (!packId) return;
    setRevising(true);
    setProblem(null);
    try {
      await revisePack(packId);
      await load();
      onPackChanged?.();
    } catch (e) {
      // The expected failure — a load has already run — arrives as a plain
      // sentence explaining what to do instead, so show it as-is.
      setProblem(errorText(e, 'Could not reopen these rules'));
    } finally {
      setRevising(false);
    }
  };

  const doApprove = async () => {
    if (!packId) return;
    setApproving(true);
    setProblem(null);
    try {
      await approvePack(packId);
      await load();
      onApproved?.();
    } catch (e) {
      setProblem(errorText(e, 'Could not approve these rules'));
    } finally {
      setApproving(false);
    }
  };

  if (!packId) {
    return (
      <p className="text-sm text-muted-foreground">
        Build the rules for a plan first — the Load a file tab has the button.
      </p>
    );
  }
  if (!data || !targets) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading the field rules…
      </div>
    );
  }

  const unmappedCount = data.counts.unmapped ?? 0;
  const loadCount = data.pack.committed_loads ?? 0;
  const rules = data.rules.filter((r) =>
    filter === 'protected'
      ? r.write_policy === 'new_only'
      : filter === 'unmapped'
        ? r.write_policy === 'unmapped'
        : true
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium">
            {data.pack.plan_name}{' '}
            <span className="text-sm font-normal text-muted-foreground">
              layout {data.pack.layout_version}
            </span>
          </p>
          <p className="text-sm text-muted-foreground">
            {data.counts.total} columns · {data.counts.protected} protected ·{' '}
            {data.counts.ignored} ignored
            {unmappedCount > 0 && (
              <span className="text-amber-600 dark:text-amber-500">
                {' '}
                · {unmappedCount} need a field
              </span>
            )}
          </p>
        </div>
        <span
          className={`rounded px-2 py-1 text-xs font-medium ${
            data.pack.status === 'approved'
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
              : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
          }`}
        >
          {data.pack.status === 'approved' ? 'Approved — in use' : 'Draft — not usable yet'}
        </span>
      </div>

      {problem && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{problem}</AlertDescription>
        </Alert>
      )}

      {!editable && (
        // Two genuinely different situations, and the earlier copy conflated them —
        // it asserted "loads have run under them" on a pack with zero loads. The
        // backend now reports committed_loads and can_reopen, so state the fact.
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertTitle>
            {data.pack.can_reopen
              ? 'These rules are approved, and no file has been loaded under them yet'
              : `These rules are fixed — ${loadCount} load${loadCount === 1 ? '' : 's'} ran under them`}
          </AlertTitle>
          <AlertDescription className="space-y-3">
            {data.pack.can_reopen ? (
              <>
                <p>
                  Nothing has been imported using them, so there is no record to protect and they
                  can be reopened — useful if you approved before deciding the unmapped columns.
                </p>
                <Button size="sm" variant="outline" onClick={doRevise} disabled={revising}>
                  {revising ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="mr-2 h-4 w-4" />
                  )}
                  Reopen for editing
                </Button>
              </>
            ) : (
              <p>
                Changing them now would make those loads&apos; records wrong, so they stay as they
                are. To change how this plan&apos;s file is read from here on, install a new layout
                version — the old rules are kept alongside the loads that used them.
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}

      {editable && unmappedCount > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {unmappedCount} column{unmappedCount === 1 ? '' : 's'} the plan sends have nowhere to go
          </AlertTitle>
          <AlertDescription>
            Pick a field for each, or set it to <em>Ignore this column</em> to say you do not want
            it. You can approve without deciding — an undecided column is simply never read — but
            then the plan's data for it is discarded silently every month.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {(
          [
            ['unmapped', `${unmappedCount} need a field`],
            ['protected', `${data.counts.protected} protected`],
            ['all', `All ${data.counts.total}`],
          ] as const
        ).map(([key, label]) => (
          <Button
            key={key}
            size="sm"
            variant={filter === key ? 'default' : 'outline'}
            onClick={() => setFilter(key)}
          >
            {label}
          </Button>
        ))}
        <div className="ml-auto">
          {editable && (
            <Button size="sm" onClick={doApprove} disabled={approving}>
              {approving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ShieldCheck className="mr-2 h-4 w-4" />
              )}
              Approve these rules
            </Button>
          )}
        </div>
      </div>

      {rules.length === 0 ? (
        <p className="rounded-md border py-8 text-center text-sm text-muted-foreground">
          {filter === 'unmapped'
            ? 'Every column the plan sends has a field or is deliberately ignored.'
            : 'Nothing to show.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[16rem]">Plan column</TableHead>
                <TableHead className="min-w-[15rem]">Our field</TableHead>
                <TableHead className="min-w-[14rem]">What the loader may do</TableHead>
                <TableHead>Why</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((r) => (
                <RuleRow
                  key={r.mif_column}
                  rule={r}
                  targets={targets}
                  editable={!!editable}
                  busy={saving === r.mif_column}
                  justSaved={!!saved[r.mif_column]}
                  onSave={(changes) => save(r.mif_column, changes)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

const RuleRow: React.FC<{
  rule: MifFieldRule;
  targets: TargetFieldOptions;
  editable: boolean;
  busy: boolean;
  justSaved: boolean;
  onSave: (changes: Partial<MifFieldRule>) => void;
}> = ({ rule, targets, editable, busy, justSaved, onSave }) => {
  // Group the picker the way the org thinks about its own fields, and mark any
  // field another column already writes to — allowed, but the last write wins.
  const grouped = useMemo(() => {
    const out: Record<string, TargetFieldOption[]> = {};
    for (const o of targets.options) (out[o.group] ||= []).push(o);
    return out;
  }, [targets]);

  // The same list, flattened for SearchableSelect. A plain <select> holding 100+ target
  // fields cannot be navigated: the browser's own type-ahead matches only from the first
  // character of a label, so finding `street_address` meant scrolling, once per MIF
  // column, 117 times. Order is preserved group by group and SearchableSelect draws a
  // heading whenever the group changes, so the optgroups survive — they are searchable now.
  const searchOptions = useMemo(
    () =>
      Object.entries(grouped).flatMap(([group, opts]) =>
        opts.map((o) => ({
          value: o.field,
          label: o.label,
          group,
          hint:
            [
              o.type !== 'core' ? o.type : null,
              o.claimed_by && o.claimed_by !== rule.mif_column ? `also ${o.claimed_by}` : null,
            ]
              .filter(Boolean)
              .join(' · ') || undefined,
        })),
      ),
    [grouped, rule.mif_column],
  );

  return (
    <TableRow className={rule.write_policy === 'unmapped' ? 'bg-amber-50/50 dark:bg-amber-950/20' : ''}>
      <TableCell className="align-top">
        <div className="font-mono text-xs">{rule.mif_column}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          column {rule.ordinal} · {rule.transform}
          {rule.data_owner && ` · ${rule.data_owner === 'org' ? 'we own this' : 'plan owns this'}`}
        </div>
      </TableCell>

      <TableCell className="align-top">
        {editable ? (
          <SearchableSelect
            id={`mif-target-${rule.mif_column}`}
            className="text-xs"
            options={searchOptions}
            value={rule.target_field ?? ''}
            disabled={busy}
            onValueChange={(v) => onSave({ target_field: v || null })}
            placeholder="— no field chosen —"
            searchPlaceholder="Type a field name…"
            emptyText="No field matches that"
          />
        ) : rule.target_field ? (
          <span className="font-mono text-xs">{rule.target_field}</span>
        ) : (
          <span className="text-xs text-amber-600 dark:text-amber-500">no field chosen</span>
        )}
        {busy && <Loader2 className="mt-1 h-3 w-3 animate-spin text-muted-foreground" />}
        {justSaved && (
          <span className="mt-1 flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
            <Check className="h-3 w-3" /> saved
          </span>
        )}
      </TableCell>

      <TableCell className="align-top">
        {editable && rule.target_field ? (
          <select
            className="w-full rounded border bg-background px-2 py-1 text-xs"
            value={rule.write_policy}
            disabled={busy}
            onChange={(e) => onSave({ write_policy: e.target.value as WritePolicy })}
          >
            {targets.write_policies.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        ) : editable ? (
          // No field yet, so the only meaningful policy choice is to disclaim the
          // column outright. Offering the write policies here would imply the
          // column does something, which it cannot.
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onSave({ write_policy: 'ignore' })}
          >
            <X className="mr-1 h-3 w-3" /> We don&apos;t want this column
          </Button>
        ) : (
          <PolicyBadge policy={rule.write_policy} />
        )}
        {editable && rule.target_field && (
          <p className="mt-1 text-xs text-muted-foreground">
            {targets.write_policies.find((p) => p.value === rule.write_policy)?.help}
          </p>
        )}
      </TableCell>

      <TableCell className="max-w-md align-top text-xs text-muted-foreground">
        {rule.rationale}
      </TableCell>
    </TableRow>
  );
};

const PolicyBadge: React.FC<{ policy: keyof typeof POLICY_LABELS }> = ({ policy }) => {
  const tone =
    policy === 'new_only'
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
      : policy === 'unmapped'
        ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
        : policy === 'ignore'
          ? 'bg-muted text-muted-foreground'
          : 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300';
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{POLICY_LABELS[policy]}</span>;
};

// ===========================================================================
// Step 2 — the file contract
// ===========================================================================

const VerifyStep: React.FC<{
  upload: UploadResult;
  onBack: () => void;
  onContinue: () => void;
  busy: boolean;
}> = ({ upload, onBack, onContinue, busy }) => {
  const c: ContractDiff = upload.contract;
  const blocked = c.severity === 'blocking';
  const needsAck = c.severity === 'acknowledge' && !c.acknowledged_at;
  // Older responses carry only consequential_missing.
  const required = c.required_missing ?? (blocked ? c.consequential_missing : []);
  const optional = c.optional_missing ?? (blocked ? [] : c.consequential_missing);
  const [agreed, setAgreed] = useState(false);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{upload.file.name}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-4">
          <Stat label="Members in file" value={upload.file.row_count} />
          <Stat label="Columns" value={`${c.actual_count} of ${c.expected_count}`} />
          <Stat label="Reporting period" value={upload.metadata.reporting_period || '—'} />
          <Stat label="Plan generated it" value={upload.metadata.file_prod_date || '—'} />
        </CardContent>
      </Card>

      {upload.already_loaded_identical_file && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>This exact file has been loaded before</AlertTitle>
          <AlertDescription>
            Load #{upload.already_loaded_identical_file.batch_id} used a file with identical contents. Loading
            it again is safe — unchanged values are skipped — but check you meant to.
          </AlertDescription>
        </Alert>
      )}

      {blocked ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>This file is missing columns it can't be loaded without</AlertTitle>
          <AlertDescription className="space-y-2">
            <ColumnList label="Required, and not in this file" columns={required} />
            <p className="text-sm">
              These identify the member, the plan or the file itself, or carry the plan's member status. Without
              them members can't be matched safely. Ask the plan for a corrected file, or point the rules at the
              new column names.
            </p>
            {optional.length > 0 && <ColumnList label="Also not in this file" columns={optional} />}
          </AlertDescription>
        </Alert>
      ) : c.severity === 'acknowledge' ? (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Some fields aren't in this file</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{c.summary}</p>
            <ColumnList label="Not in this file, and mapped to one of your fields" columns={optional} />
            {c.added.length > 0 && <ColumnList label="New columns the rules don't cover yet" columns={c.added} />}
            <p className="text-sm">
              Those fields are left exactly as they are for every member in the file. Everything else loads
              as usual, and you'll see what would change before anything is saved.
            </p>
            {needsAck ? (
              <label htmlFor="mif-ack-missing" className="flex items-start gap-2 text-sm font-medium">
                <input
                  id="mif-ack-missing"
                  type="checkbox"
                  className="mt-0.5"
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                />
                I understand these fields won't be updated by this file
              </label>
            ) : (
              <p className="text-sm">Acknowledged{c.acknowledged_at ? ` ${new Date(c.acknowledged_at).toLocaleString()}` : ''}.</p>
            )}
          </AlertDescription>
        </Alert>
      ) : (
        <Alert>
          {c.severity === 'clean' ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          <AlertTitle>
            {c.severity === 'clean' ? 'The file matches the approved layout' : 'The layout has changed since last month'}
          </AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{c.summary}</p>
            {c.added.length > 0 && <ColumnList label="New columns the rules don't cover yet" columns={c.added} />}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} disabled={busy}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Discard this file and choose another
        </Button>
        <Button onClick={onContinue} disabled={busy || blocked || (needsAck && !agreed)}>
          See what this would change <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const ColumnList: React.FC<{ label: string; columns: string[] }> = ({ label, columns }) => (
  <div className="text-sm">
    <span className="font-medium">{label}:</span>{' '}
    <span className="font-mono text-xs">{columns.join(', ')}</span>
  </div>
);

const Stat: React.FC<{ label: string; value: React.ReactNode; tone?: 'good' | 'warn' | 'protect' }> = ({
  label,
  value,
  tone,
}) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p
      className={`mt-0.5 text-xl font-semibold tabular-nums ${
        tone === 'protect'
          ? 'text-amber-600 dark:text-amber-500'
          : tone === 'warn'
            ? 'text-red-600 dark:text-red-400'
            : tone === 'good'
              ? 'text-emerald-600 dark:text-emerald-400'
              : ''
      }`}
    >
      {value}
    </p>
  </div>
);

// ===========================================================================
// Step 4 — the impact review
// ===========================================================================

const ReviewStep: React.FC<{
  summary: PlanSummary;
  rows: MifRow[];
  gapCount: number;
  onBack: () => void;
  onResolve: () => void;
  onCommit: () => void;
  busy: boolean;
}> = ({ summary, rows, gapCount, onBack, onResolve, onCommit, busy }) => {
  const [view, setView] = useState<'changes' | 'protected'>('changes');
  const shown = rows.filter((r) => (view === 'changes' ? r.writes.length > 0 : r.protected.length > 0));

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="grid gap-4 pt-6 sm:grid-cols-5">
          <Stat label="New members" value={summary.rows_to_create} tone="good" />
          <Stat label="To update" value={summary.rows_to_update} />
          <Stat label="Unchanged" value={summary.rows_no_change} />
          <Stat label="Need review" value={summary.rows_needing_review} tone={gapCount ? 'warn' : undefined} />
          <Stat label="Fields protected" value={summary.cells_protected} tone="protect" />
        </CardContent>
      </Card>

      {summary.cells_protected > 0 && (
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertTitle>
            {summary.cells_protected} field value{summary.cells_protected === 1 ? '' : 's'} will be left as
            your staff entered them
          </AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              The plan's file carries its own copy of these, taken from what you reported last month. Your
              current values are newer, so the load leaves them alone.
            </p>
            {summary.protected_fields.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {summary.protected_fields.slice(0, 12).map(([field, count]) => (
                  <span
                    key={field}
                    className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                  >
                    {field} · {count}
                  </span>
                ))}
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {gapCount > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {gapCount} row{gapCount === 1 ? '' : 's'} the load cannot decide on its own
          </AlertTitle>
          <AlertDescription className="space-y-2">
            <p>These are skipped unless you tell the load what to do with them.</p>
            <Button size="sm" variant="outline" onClick={onResolve}>
              <ClipboardList className="mr-2 h-4 w-4" /> Work through them
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {summary.churn_candidates > 0 && (
        <Alert>
          <Users className="h-4 w-4" />
          <AlertTitle>
            {summary.churn_candidates} member{summary.churn_candidates === 1 ? '' : 's'} are not in this file
          </AlertTitle>
          <AlertDescription>
            They stay exactly as they are. Nobody is disenrolled by a load — you can review them afterwards.
          </AlertDescription>
        </Alert>
      )}

      {summary.identity_coverage.members_without_cin > 0 && (
        <p className="text-sm text-muted-foreground">
          {summary.identity_coverage.members_without_cin} of your{' '}
          {summary.identity_coverage.members_total} members have no CIN on record, so they can only ever be
          matched by name and date of birth.
        </p>
      )}

      <div className="flex gap-2">
        <Button size="sm" variant={view === 'changes' ? 'default' : 'outline'} onClick={() => setView('changes')}>
          Changes ({rows.filter((r) => r.writes.length).length} members)
        </Button>
        <Button
          size="sm"
          variant={view === 'protected' ? 'default' : 'outline'}
          onClick={() => setView('protected')}
        >
          Protected ({rows.filter((r) => r.protected.length).length} members)
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Row</TableHead>
              <TableHead>Member</TableHead>
              <TableHead className="w-28">Match</TableHead>
              <TableHead>{view === 'changes' ? 'Fields the plan will update' : 'Fields left alone'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                  {view === 'changes' ? 'Nothing would change.' : 'No fields needed protecting.'}
                </TableCell>
              </TableRow>
            )}
            {shown.map((r) => (
              <TableRow key={r.row_number}>
                <TableCell className="tabular-nums text-xs">{r.row_number}</TableCell>
                <TableCell className="text-sm">
                  {r.member_name || `${r.file_first_name || ''} ${r.file_last_name || ''}`.trim() || '—'}
                  {r.mif_cin && <div className="font-mono text-xs text-muted-foreground">{r.mif_cin}</div>}
                </TableCell>
                <TableCell>
                  <ClassificationBadge value={r.classification} method={r.match_method} />
                </TableCell>
                <TableCell>
                  <DecisionList decisions={view === 'changes' ? r.writes : r.protected} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <Button onClick={onCommit} disabled={busy}>
          Load {summary.rows_to_create + summary.rows_to_update} member
          {summary.rows_to_create + summary.rows_to_update === 1 ? '' : 's'}
        </Button>
      </div>
    </div>
  );
};

const ClassificationBadge: React.FC<{ value: string; method?: string | null }> = ({ value, method }) => {
  const map: Record<string, [string, string]> = {
    new: ['New', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'],
    matched: ['Matched', 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'],
    ambiguous: ['Ambiguous', 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'],
    unmatched: ['No match', 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'],
    duplicate: ['Repeated', 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300'],
  };
  const [label, tone] = map[value] || [value, 'bg-muted text-muted-foreground'];
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${tone}`}>
      {label}
      {method === 'cin' ? ' · CIN' : method === 'name_dob' ? ' · name+DOB' : ''}
    </span>
  );
};

const DecisionList: React.FC<{ decisions: CellDecision[] }> = ({ decisions }) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? decisions : decisions.slice(0, 4);
  return (
    <div className="space-y-1">
      {shown.map((d, i) => (
        <div key={`${d.mif_column}-${i}`} className="text-xs">
          <span className="font-medium">{d.label || d.field}</span>
          {d.action === 'write' ? (
            <span className="text-muted-foreground">
              {' '}
              <span className="line-through">{d.old || 'empty'}</span> → <span className="text-foreground">{d.new}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">
              {' '}
              keeping <span className="text-foreground">{d.old || 'empty'}</span> · {ACTION_LABELS[d.action]}
            </span>
          )}
        </div>
      ))}
      {decisions.length > 4 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-primary underline-offset-2 hover:underline"
        >
          {expanded ? 'Show fewer' : `+${decisions.length - 4} more`}
        </button>
      )}
    </div>
  );
};

// ===========================================================================
// Step 5 — the gaps queue
// ===========================================================================

const ResolveStep: React.FC<{
  batchId: number;
  gaps: MifGap[];
  onBack: () => void;
  onReplan: () => void;
}> = ({ batchId, gaps, onBack, onReplan }) => {
  const [done, setDone] = useState<Record<number, string>>({});

  const act = async (
    row: number,
    resolution: 'link_existing' | 'create_new' | 'skip',
    memberId?: number
  ) => {
    try {
      await resolveGap(batchId, row, { resolution, member_id: memberId });
      setDone((d) => ({ ...d, [row]: resolution }));
    } catch (e) {
      /* the row stays open; the operator can retry */
    }
  };

  const outstanding = gaps.filter((g) => !done[g.row_number]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Rows the load will not guess at</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          A wrong match merges two people's records, which is much harder to undo than answering a question.
          Anything left unanswered is skipped, not guessed.
        </p>
      </div>

      {outstanding.length === 0 ? (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>All answered</AlertTitle>
          <AlertDescription>
            Run the review again so the numbers reflect your answers.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-3">
          {outstanding.map((g) => (
            <GapCard key={g.row_number} gap={g} batchId={batchId} onAct={act} />
          ))}
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to the review
        </Button>
        <Button onClick={onReplan}>Update the review with my answers</Button>
      </div>
    </div>
  );
};

const GapCard: React.FC<{
  gap: MifGap;
  batchId: number;
  onAct: (row: number, resolution: 'link_existing' | 'create_new' | 'skip', memberId?: number) => void;
}> = ({ gap, batchId, onAct }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const name = `${gap.file_first_name || ''} ${gap.file_last_name || ''}`.trim() || 'Unnamed row';

  const doSearch = async () => {
    if (query.trim().length < 2) return;
    try {
      setResults((await searchMembers(batchId, query)).members);
    } catch {
      setResults([]);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3 pt-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-medium">
              Row {gap.row_number} · {name}
            </p>
            <p className="text-xs text-muted-foreground">
              {gap.file_dob && <>born {gap.file_dob} · </>}
              {gap.mif_cin ? <>CIN {gap.mif_cin}</> : 'no CIN in the file'}
            </p>
          </div>
          <ClassificationBadge value={gap.classification} method={gap.match_method} />
        </div>

        {gap.errors && gap.errors.length > 0 && (
          <div className="rounded bg-muted p-2 text-xs">
            {gap.errors.map((e, i) => (
              <div key={i}>
                <span className="font-mono">{e.mif_column}</span>: {e.error}
              </div>
            ))}
          </div>
        )}

        {gap.candidates && gap.candidates.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Possible matches already on file
            </p>
            {gap.candidates.map((c) => (
              <div key={c.member_id} className="flex items-center justify-between rounded border p-2 text-sm">
                <span>
                  {c.name || `Member ${c.member_id}`}
                  {c.dob && <span className="text-muted-foreground"> · born {c.dob}</span>}
                  {c.cin && <span className="ml-2 font-mono text-xs text-muted-foreground">{c.cin}</span>}
                </span>
                <Button size="sm" variant="outline" onClick={() => onAct(gap.row_number, 'link_existing', c.member_id)}>
                  This is them
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doSearch()}
            placeholder="Search your members by name or CIN"
            className="max-w-xs"
          />
          <Button size="sm" variant="outline" onClick={doSearch}>
            Search
          </Button>
        </div>
        {results.map((m) => (
          <div key={m.id} className="flex items-center justify-between rounded border p-2 text-sm">
            <span>
              {m.first_name} {m.last_name}
              {m.dob && <span className="text-muted-foreground"> · born {m.dob}</span>}
              {m.cin && <span className="ml-2 font-mono text-xs text-muted-foreground">{m.cin}</span>}
            </span>
            <Button size="sm" variant="outline" onClick={() => onAct(gap.row_number, 'link_existing', m.id)}>
              Link
            </Button>
          </div>
        ))}

        <div className="flex flex-wrap gap-2 border-t pt-3">
          <Button size="sm" onClick={() => onAct(gap.row_number, 'create_new')}>
            <UserPlus className="mr-2 h-4 w-4" /> Add as a new member
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onAct(gap.row_number, 'skip')}>
            <X className="mr-2 h-4 w-4" /> Leave this row out
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ===========================================================================
// Step 7 — results, churn review, rollback
// ===========================================================================

const ResultsStep: React.FC<{
  result: any;
  churn: ChurnItem[];
  batchId: number;
  rollbackPreview: RollbackPreview | null;
  setRollbackPreview: (p: RollbackPreview | null) => void;
  onDone: () => void;
  onRolledBack: () => void;
}> = ({ result, churn, batchId, rollbackPreview, setRollbackPreview, onDone, onRolledBack }) => {
  const [busy, setBusy] = useState(false);
  const [includeConflicts, setIncludeConflicts] = useState(false);

  const askRollback = async () => {
    setBusy(true);
    try {
      setRollbackPreview(await previewRollback(batchId));
    } finally {
      setBusy(false);
    }
  };

  const doRollback = async () => {
    setBusy(true);
    try {
      await rollbackBatch(batchId, { include_conflicts: includeConflicts });
      onRolledBack();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Alert>
        <CheckCircle2 className="h-4 w-4" />
        <AlertTitle>Loaded</AlertTitle>
        <AlertDescription>{result.message}</AlertDescription>
      </Alert>

      <Card>
        <CardContent className="grid gap-4 pt-6 sm:grid-cols-4">
          <Stat label="Members added" value={result.members_created} tone="good" />
          <Stat label="Members updated" value={result.members_updated} />
          <Stat label="Fields written" value={result.cells_written} />
          <Stat label="Fields protected" value={result.cells_protected} tone="protect" />
        </CardContent>
      </Card>

      {churn.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {churn.length} member{churn.length === 1 ? '' : 's'} not in this file
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-sm text-muted-foreground">
              Nothing has changed for them. Mark what you find so next month's list is shorter.
            </p>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>CIN</TableHead>
                    <TableHead className="w-64">What did you find?</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {churn.map((c) => (
                    <ChurnRow key={c.id} item={c} batchId={batchId} />
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <RotateCcw className="h-4 w-4" /> Undo this load
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!rollbackPreview ? (
            <>
              <p className="text-sm text-muted-foreground">
                Every value this load wrote was recorded first, so it can be put back.
              </p>
              <Button variant="outline" size="sm" onClick={askRollback} disabled={busy}>
                See what undoing would do
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm">{rollbackPreview.message}</p>
              {rollbackPreview.conflicts.length > 0 && (
                <div className="space-y-2 rounded border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
                  <p className="text-sm font-medium">
                    {rollbackPreview.conflicts.length} field
                    {rollbackPreview.conflicts.length === 1 ? '' : 's'} changed since the load
                  </p>
                  <div className="max-h-40 space-y-1 overflow-y-auto text-xs">
                    {rollbackPreview.conflicts.slice(0, 20).map((c, i) => (
                      <div key={i}>
                        {c.member_name} · <span className="font-mono">{c.field_name}</span> — now{' '}
                        <span className="font-medium">{c.current_value}</span>, changed by {c.last_updated_by}
                      </div>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={includeConflicts}
                      onChange={(e) => setIncludeConflicts(e.target.checked)}
                    />
                    Revert these too, discarding those changes
                  </label>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="destructive" size="sm" onClick={doRollback} disabled={busy}>
                  Undo this load
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setRollbackPreview(null)}>
                  Keep it
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Button onClick={onDone}>Load another file</Button>
    </div>
  );
};

const ChurnRow: React.FC<{ item: ChurnItem; batchId: number }> = ({ item, batchId }) => {
  const [outcome, setOutcome] = useState(item.outcome);
  const mark = async (value: 'still_enrolled' | 'disenrolled' | 'unknown') => {
    const { reviewChurn } = await import('@/services/api/mifImport');
    await reviewChurn(batchId, item.id, value);
    setOutcome(value);
  };
  return (
    <TableRow>
      <TableCell className="text-sm">{item.member_name}</TableCell>
      <TableCell className="font-mono text-xs">{item.member_cin}</TableCell>
      <TableCell>
        {outcome ? (
          <Badge variant="secondary">{outcome.replace('_', ' ')}</Badge>
        ) : (
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => mark('still_enrolled')}>
              Still enrolled
            </Button>
            <Button size="sm" variant="outline" onClick={() => mark('disenrolled')}>
              Disenrolled
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

// ===========================================================================
// Reusing a pack at another organization (steps 5-6)
// ===========================================================================

/**
 * Platform-staff tooling. Cloning reads one organization's configuration into
 * another, crossing the isolation boundary the platform is otherwise strict about,
 * so the backend limits it to super_admin / ops_admin and this panel is hidden
 * otherwise. The hiding is courtesy; the backend is the gate.
 *
 * The flow is deliberately preview-first, and in this order:
 *
 *   1. see what would resolve at the destination
 *   2. create the fields it is missing        <- the step that makes this worth doing
 *   3. clone
 *
 * Measured on production, cloning IFR's pack to the next-nearest ECM organization
 * resolves 12 of 110 targets, because 98 of the fields do not exist there. Cloning
 * first and discovering that afterwards is the failure this ordering prevents.
 */
const ClonePanel: React.FC<{ packId?: number; packStatus?: string }> = ({ packId, packStatus }) => {
  const [orgs, setOrgs] = useState<{ id: number; name: string }[] | null>(null);
  const [destination, setDestination] = useState<number | null>(null);
  const [preview, setPreview] = useState<ClonePreview | null>(null);
  const [gap, setGap] = useState<FieldGap | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    organizationsAPI
      .listOrganizations()
      .then((list) =>
        setOrgs(
          (list || [])
            .map((o: any) => ({ id: o.id, name: o.name }))
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
        )
      )
      .catch(() => setOrgs([]));
  }, []);

  const runPreview = async (destinationId: number) => {
    if (!packId) return;
    setBusy('preview');
    setProblem(null);
    setGap(null);
    setDone(null);
    try {
      const p = await clonePreview(packId, destinationId);
      setPreview(p);
      if (p.missing_fields.length) {
        setGap(
          await provisionFields(
            destinationId,
            p.source_pack.cbo_id,
            p.missing_fields.map((f) => f.field),
            false
          )
        );
      }
    } catch (e) {
      setProblem(errorText(e, 'Could not work out what cloning would do'));
    } finally {
      setBusy(null);
    }
  };

  const doProvision = async () => {
    if (!preview || !destination || !gap) return;
    setBusy('provision');
    setProblem(null);
    try {
      const r = await provisionFields(
        destination,
        preview.source_pack.cbo_id,
        gap.missing.map((f) => f.name),
        true
      );
      setDone(r.message || 'Fields created.');
      await runPreview(destination);
    } catch (e) {
      setProblem(errorText(e, 'Could not create the fields'));
    } finally {
      setBusy(null);
    }
  };

  const doClone = async () => {
    if (!packId || !destination) return;
    setBusy('clone');
    setProblem(null);
    try {
      const r = await clonePack(packId, destination);
      setDone(r.message);
      await runPreview(destination);
    } catch (e) {
      setProblem(errorText(e, 'Could not clone the pack'));
    } finally {
      setBusy(null);
    }
  };

  if (!packId) {
    return (
      <p className="text-sm text-muted-foreground">
        Build a set of rules first — there is nothing to copy yet.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold">Reuse these rules at another organization</h3>
        <p className="text-sm text-muted-foreground">
          The parts that come from the health plan — the columns, how each value is read, and which
          ones are protected — apply anywhere. Which of your fields each column writes to does not,
          so that is worked out again for the destination.
        </p>
      </div>

      {packStatus !== 'approved' && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            These rules are still a draft. You can copy them, but it is usually better to finish and
            approve them first so the destination starts from something settled.
          </AlertDescription>
        </Alert>
      )}

      {problem && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{problem}</AlertDescription>
        </Alert>
      )}
      {done && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{done}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {/* Every organisation on the platform, so the same type-ahead the field picker
            needs applies here — scrolling a tenant list by eye is the same problem. */}
        <SearchableSelect
          id="mif-clone-destination"
          className="min-w-[16rem]"
          options={(orgs || []).map((o) => ({ value: String(o.id), label: o.name }))}
          value={destination != null ? String(destination) : ''}
          onValueChange={(v) => {
            const id = v ? Number(v) : null;
            setDestination(id);
            setPreview(null);
            setGap(null);
            setDone(null);
            if (id) runPreview(id);
          }}
          placeholder="Choose the organization to copy to…"
          searchPlaceholder="Type an organization name…"
          emptyText="No organization matches that"
        />
        {busy === 'preview' && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {preview && (
        <div className="space-y-4">
          <Card>
            <CardContent className="grid gap-4 pt-6 sm:grid-cols-3">
              <Stat label="Columns from the plan" value={preview.columns_total} />
              <Stat label="Would find a field" value={preview.would_resolve} tone="good" />
              <Stat
                label="Would have nowhere to go"
                value={preview.would_be_unmapped}
                tone={preview.would_be_unmapped ? 'warn' : undefined}
              />
            </CardContent>
          </Card>

          <p className="text-sm">{preview.message}</p>

          {preview.destination_has_packs.length > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {preview.destination_name} already has{' '}
                {preview.destination_has_packs.map((p) => `${p.layout_version} (${p.status})`).join(', ')}{' '}
                for this plan. A copy always arrives as a draft, so nothing in use changes.
              </AlertDescription>
            </Alert>
          )}

          {gap && gap.missing.length > 0 && (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-950/30">
              <p className="text-sm font-medium">
                {gap.missing.length} field{gap.missing.length === 1 ? '' : 's'} would need creating on{' '}
                {preview.destination_name}
              </p>
              <p className="text-xs text-muted-foreground">
                Without these the copy arrives mostly unmapped. Creating them changes what a member
                record holds for that organization, so it is a separate step — and it only ever adds,
                never changes or removes what is already there.
              </p>
              <div className="max-h-56 overflow-y-auto rounded border bg-background">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Field</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Size</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gap.missing.map((f) => (
                      <TableRow key={f.name}>
                        <TableCell className="font-mono text-xs">
                          {f.name}
                          {f.width_note && (
                            <div className="mt-0.5 max-w-md text-xs font-sans text-amber-700 dark:text-amber-500">
                              {f.width_note}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{f.type}</TableCell>
                        <TableCell className="text-right tabular-nums text-xs">
                          {f.str_len}
                          {f.str_len !== f.declared_str_len && (
                            <span className="text-muted-foreground"> (was {f.declared_str_len})</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Button size="sm" onClick={doProvision} disabled={busy === 'provision'}>
                {busy === 'provision' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Create these {gap.missing.length} field{gap.missing.length === 1 ? '' : 's'}
              </Button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={doClone} disabled={busy === 'clone' || !destination}>
              {busy === 'clone' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Copy the rules to {preview.destination_name}
            </Button>
            {preview.would_be_unmapped > 0 && (
              <span className="text-xs text-muted-foreground">
                You can copy now — {preview.would_be_unmapped} columns will simply be marked as
                needing a field.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// Layouts — the plan's file shape, and where a new plan is onboarded
// ===========================================================================

/**
 * A layout describes one health plan's file at one revision. A pack is that layout
 * resolved against THIS organization's fields, which is why layouts are portable
 * and packs are not.
 *
 * This is the screen that was missing: there was no way to see which layout
 * versions existed, install a newer one, or onboard a plan at all — `planKey` was
 * hardcoded with no way to change it.
 *
 * `origin` is shown prominently on purpose. A layout hand-verified against real
 * files and one a machine inferred from a single upload must never look the same to
 * whoever approves a pack from it.
 */
const LayoutsPanel: React.FC<{
  planKey: string;
  onPlanKeyChange: (key: string) => void;
  onPackInstalled: () => void;
}> = ({ planKey, onPlanKeyChange, onPackInstalled }) => {
  const [layouts, setLayouts] = useState<MifLayout[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [deriving, setDeriving] = useState(false);
  const [newPlan, setNewPlan] = useState({ plan_key: '', plan_name: '', layout_version: '' });
  const { t } = useAppTranslation();
  // TRUS-727: open on "My Layouts" once the org has any, otherwise on the catalog.
  const [view, setView] = useState<LayoutView | null>(null);
  const [catalogState, setCatalogState] = useState(DEFAULT_STATE);
  const [catalogPlan, setCatalogPlan] = useState(ALL_PLANS);
  const [catalog, setCatalog] = useState<MifLayout[] | null>(null);

  const load = useCallback(async () => {
    try {
      const all = (await listLayouts()).layouts;
      setLayouts(all);
      setView((v) => v ?? (mineOnly(all).length ? 'mine' : 'catalog'));
    } catch (e) {
      setProblem(errorText(e, t('integrations.mif_layouts.load_failed', 'Could not load the layouts')));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (view !== 'catalog') return;
    let cancelled = false;
    listLayouts({ state: catalogState, plan: catalogPlan || undefined })
      .then((r) => {
        if (!cancelled) setCatalog(r.layouts);
      })
      .catch((e) => {
        if (!cancelled) setProblem(errorText(e, t('integrations.mif_layouts.load_failed', 'Could not load the layouts')));
      });
    return () => {
      cancelled = true;
    };
  }, [view, catalogState, catalogPlan, layouts, t]);

  const install = async (layout: MifLayout) => {
    setBusy(`install-${layout.id}`);
    setProblem(null);
    try {
      const r = await installPack(layout.plan_key, layout.id);
      setResult(r.message);
      onPlanKeyChange(layout.plan_key);
      onPackInstalled();
      await load();
    } catch (e) {
      setProblem(errorText(e, 'Could not install a pack from that layout'));
    } finally {
      setBusy(null);
    }
  };

  const publish = async (layout: MifLayout, status: 'published' | 'retired') => {
    setBusy(`status-${layout.id}`);
    setProblem(null);
    try {
      await setLayoutStatus(layout.id, status);
      await load();
    } catch (e) {
      setProblem(errorText(e, `Could not ${status === 'published' ? 'publish' : 'retire'} that layout`));
    } finally {
      setBusy(null);
    }
  };

  const onDrop = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      if (!newPlan.plan_key || !newPlan.plan_name || !newPlan.layout_version) {
        setProblem('Give the plan a short name, a full name, and a version label first.');
        return;
      }
      setDeriving(true);
      setProblem(null);
      try {
        const r = await deriveLayout(files[0], newPlan);
        setResult(
          `${r.message} ${r.summary.high_confidence} of ${r.summary.columns} columns were read ` +
            `with high confidence. Review it, then publish to make it installable.`
        );
        setNewPlan({ plan_key: '', plan_name: '', layout_version: '' });
        await load();
      } catch (e) {
        setProblem(errorText(e, 'Could not read a layout from that file'));
      } finally {
        setDeriving(false);
      }
    },
    [newPlan, load]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    disabled: deriving,
  });

  if (!layouts) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading layouts…
      </div>
    );
  }

  const shown = view === 'mine' ? mineOnly(layouts) : catalog ?? [];
  const byPlan = shown.reduce<Record<string, MifLayout[]>>((acc, l) => {
    (acc[l.plan_key] ||= []).push(l);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {problem && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{problem}</AlertDescription>
        </Alert>
      )}
      {result && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{result}</AlertDescription>
        </Alert>
      )}

      <section className="space-y-3">
        <div>
          <h3 className="text-base font-semibold">Plan file layouts</h3>
          <p className="text-sm text-muted-foreground">
            A layout is what the plan sends. Installing one creates a draft set of rules for your
            organization, mapped to your own fields.
          </p>
        </div>

        <MifLayoutCatalogControls
          view={view ?? 'catalog'}
          onViewChange={setView}
          mineCount={mineOnly(layouts).length}
          states={statesFrom(layouts)}
          state={catalogState}
          onStateChange={(s) => {
            setCatalogState(s);
            setCatalogPlan(ALL_PLANS);
          }}
          plans={plansIn(layouts, catalogState)}
          plan={catalogPlan}
          onPlanChange={setCatalogPlan}
        />

        {Object.keys(byPlan).length === 0 && (
          <p className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
            {view === 'mine'
              ? t('integrations.mif_layouts.mine_empty', 'No layouts yet. Install one from the catalog.')
              : t('integrations.mif_layouts.catalog_empty', 'No layouts for this state and plan.')}
          </p>
        )}

        {Object.entries(byPlan).map(([key, versions]) => (
          <div key={key} className="rounded-md border">
            <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-2">
              <span className="font-medium">{versions[0].plan_name}</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-muted-foreground">{key}</span>
                {planKey === key && <Badge variant="secondary">selected</Badge>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Version</TableHead>
                    <TableHead>Where it came from</TableHead>
                    <TableHead className="text-right">Columns</TableHead>
                    <TableHead className="text-right">Protected</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-56"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versions.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="font-mono text-xs">{l.layout_version}</TableCell>
                      <TableCell>
                        <OriginBadge origin={l.origin} />
                        {l.source_note && (
                          <div
                            className="mt-0.5 max-w-md truncate text-xs text-muted-foreground"
                            title={l.source_note}
                          >
                            {l.source_note}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{l.column_count}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{l.protected_count}</TableCell>
                      <TableCell>
                        <LayoutStatusBadge status={l.status} />
                        {l.packs_here > 0 && (
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {l.packs_here} pack{l.packs_here === 1 ? '' : 's'} here
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {l.status === 'draft' && l.cbo_id !== null && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy === `status-${l.id}`}
                              onClick={() => publish(l, 'published')}
                            >
                              Publish
                            </Button>
                          )}
                          {l.status === 'published' && (
                            <Button
                              size="sm"
                              disabled={busy === `install-${l.id}`}
                              onClick={() => install(l)}
                            >
                              {busy === `install-${l.id}` ? (
                                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                              ) : null}
                              {l.packs_here > 0 ? 'Reinstall' : 'Install'}
                            </Button>
                          )}
                          {planKey !== key && (
                            <Button size="sm" variant="ghost" onClick={() => onPlanKeyChange(key)}>
                              Use this plan
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ))}
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-base font-semibold">Onboard a new plan from its file</h3>
          <p className="text-sm text-muted-foreground">
            Upload a file from a plan we do not have a layout for. The columns, their order and how
            each value should be read are worked out from the file itself.
          </p>
        </div>

        <Alert>
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>Everything comes back protected, on purpose</AlertTitle>
          <AlertDescription>
            A file cannot tell us which columns the plan is echoing back from your own reporting —
            which is exactly the data that must not be overwritten. So every column starts as
            protected, and you decide which ones the plan owns before approving a pack.
          </AlertDescription>
        </Alert>

        <div className="grid gap-2 sm:grid-cols-3">
          <Input
            placeholder="Short name, e.g. healthnet"
            value={newPlan.plan_key}
            onChange={(e) =>
              setNewPlan((p) => ({ ...p, plan_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))
            }
          />
          <Input
            placeholder="Full name, e.g. Health Net"
            value={newPlan.plan_name}
            onChange={(e) => setNewPlan((p) => ({ ...p, plan_name: e.target.value }))}
          />
          <Input
            placeholder="Version label, e.g. 2026-08"
            value={newPlan.layout_version}
            onChange={(e) => setNewPlan((p) => ({ ...p, layout_version: e.target.value }))}
          />
        </div>

        <div
          {...getRootProps()}
          className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
            isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
          } ${deriving ? 'opacity-60' : ''}`}
        >
          <input {...getInputProps()} />
          {deriving ? (
            <div className="flex items-center justify-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading the file…
            </div>
          ) : (
            <>
              <FileSpreadsheet className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-2 text-sm font-medium">Drop a file from the new plan</p>
              <p className="text-xs text-muted-foreground">
                Nothing is imported — this only works out the file&apos;s shape.
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

const OriginBadge: React.FC<{ origin: string }> = ({ origin }) => {
  const map: Record<string, [string, string, string]> = {
    shipped: [
      'Verified',
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
      'Hand-checked against real files from this plan',
    ],
    derived: [
      'Read from a file',
      'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
      'Worked out from one upload — review before relying on it',
    ],
    authored: [
      'Edited here',
      'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
      'Created or changed by someone in your organization',
    ],
  };
  const [label, tone, help] = map[origin] || [origin, 'bg-muted text-muted-foreground', ''];
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${tone}`}
      title={help}
    >
      {label}
    </span>
  );
};

const LayoutStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, [string, string]> = {
    published: ['Installable', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'],
    draft: ['Draft', 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'],
    retired: ['Retired', 'bg-muted text-muted-foreground'],
  };
  const [label, tone] = map[status] || [status, 'bg-muted text-muted-foreground'];
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{label}</span>;
};

// ===========================================================================
// How the rules changed, and what the plan changed
// ===========================================================================

/**
 * Health plans revise their layouts without notice, so a pack is not a one-time
 * setup. Two questions have to stay answerable long after the decision was made:
 * what did WE change, and what did THE PLAN change.
 *
 * They are deliberately one screen. Reading "the plan started sending
 * Date_Member_Notified_of_Authorization in March" next to "someone mapped it in
 * April" is the whole point; split across two tabs the connection is lost.
 */
const ChangesPanel: React.FC<{ packId?: number; planKey?: string }> = ({ packId, planKey }) => {
  const [history, setHistory] = useState<RuleChange[] | null>(null);
  const [proposalList, setProposalList] = useState<AgentProposal[] | null>(null);
  const [drift, setDrift] = useState<LayoutDrift[] | null>(null);
  const [summary, setSummary] = useState<DriftSummary | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [d, h, pr] = await Promise.all([
        getDrift(planKey),
        packId ? getPackHistory(packId) : Promise.resolve({ history: [] }),
        listProposals(packId),
      ]);
      setDrift(d.drift);
      setSummary(d.summary);
      setHistory(h.history);
      setProposalList(pr.proposals);
    } catch (e) {
      setProblem(errorText(e, 'Could not load the change history'));
    }
  }, [packId, planKey]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (
    id: number,
    status: 'acknowledged' | 'resolved' | 'dismissed'
  ) => {
    setBusy(id);
    try {
      await decideDrift(id, { status, pack_id: packId });
      await load();
    } catch (e) {
      setProblem(errorText(e, 'Could not update that item'));
    } finally {
      setBusy(null);
    }
  };

  if (!history || !drift) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading the change history…
      </div>
    );
  }

  const open = drift.filter((d) => d.status === 'open' || d.status === 'acknowledged');
  const closed = drift.filter((d) => d.status === 'resolved' || d.status === 'dismissed');

  const decideOnProposal = async (id: number, accept: boolean) => {
    setBusy(id);
    try {
      await decideProposal(id, accept);
      await load();
    } catch (e) {
      setProblem(errorText(e, 'Could not record that decision'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      {problem && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{problem}</AlertDescription>
        </Alert>
      )}

      {proposalList && proposalList.length > 0 && (
        // First, because a pending decision is the only thing on this screen that
        // blocks anyone. The agent's row has changed nothing — accepting is what
        // applies it.
        <section className="space-y-3">
          <div>
            <h3 className="text-base font-semibold">
              {proposalList.length} suggestion{proposalList.length === 1 ? '' : 's'} waiting on you
            </h3>
            <p className="text-sm text-muted-foreground">
              Nothing has been changed. The assistant reviewed the plan&apos;s layout and suggests
              these; each one applies only if you accept it.
            </p>
          </div>

          <div className="space-y-2">
            {proposalList.map((p) => (
              <Card key={p.id}>
                <CardContent className="space-y-2 pt-4">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-mono text-xs">{p.mif_column}</span>
                    {p.target_field && (
                      <span className="text-xs text-muted-foreground">→ {p.target_field}</span>
                    )}
                  </div>
                  <div className="text-sm">
                    {describeAttribute(p.attribute)}:{' '}
                    <span className="text-muted-foreground line-through">
                      {readablePolicy(p.old_value)}
                    </span>{' '}
                    → <span className="font-medium">{readablePolicy(p.new_value)}</span>
                  </div>
                  {p.note && (
                    <p className="rounded bg-muted/60 p-2 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">Why: </span>
                      {p.note}
                    </p>
                  )}
                  {p.old_value === 'new_only' && (
                    // The one case worth a warning: accepting removes a protection.
                    <p className="text-xs text-amber-700 dark:text-amber-500">
                      This column is currently protected. Accepting lets the plan overwrite what your
                      staff have entered in it — only do that if the plan really is the source of
                      truth for this field.
                    </p>
                  )}
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" disabled={busy === p.id} onClick={() => decideOnProposal(p.id, true)}>
                      {busy === p.id ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy === p.id}
                      onClick={() => decideOnProposal(p.id, false)}
                    >
                      No, leave it
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <div>
          <h3 className="text-base font-semibold">What the plan changed</h3>
          <p className="text-sm text-muted-foreground">
            Counted across every file, not per upload — a column the plan has been sending for six
            months is one item here, with how many times it has appeared.
          </p>
        </div>

        {summary && summary.open_blocking > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {summary.open_blocking} column{summary.open_blocking === 1 ? '' : 's'} you rely on
              stopped arriving
            </AlertTitle>
            <AlertDescription>
              A load would simply stop updating {summary.open_blocking === 1 ? 'that field' : 'those fields'},
              with nothing in the result to say so.
            </AlertDescription>
          </Alert>
        )}

        {open.length === 0 ? (
          <p className="rounded-md border py-6 text-center text-sm text-muted-foreground">
            The plan&apos;s files have matched the approved layout every time.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>What changed</TableHead>
                  <TableHead>Column</TableHead>
                  <TableHead className="text-right">Times seen</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="w-64">Decide</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {open.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <DriftBadge type={d.drift_type} severity={d.severity} />
                      {d.status === 'acknowledged' && (
                        <div className="mt-1 text-xs text-muted-foreground">acknowledged</div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{d.mif_column}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{d.times_seen}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {d.last_seen_period || new Date(d.last_seen_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {d.status === 'open' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy === d.id}
                            onClick={() => decide(d.id, 'acknowledged')}
                          >
                            Seen it
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy === d.id}
                          onClick={() => decide(d.id, 'resolved')}
                        >
                          Handled
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy === d.id}
                          onClick={() => decide(d.id, 'dismissed')}
                        >
                          Not needed
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {closed.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {closed.length} closed item{closed.length === 1 ? '' : 's'}. If the plan does one of them
            again it reopens here.
          </p>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-base font-semibold">How your rules changed</h3>
          <p className="text-sm text-muted-foreground">
            Every install, edit, approval and reopen — including changes the system derived rather
            than you typing them.
          </p>
        </div>

        {history.length === 0 ? (
          <p className="rounded-md border py-6 text-center text-sm text-muted-foreground">
            No changes recorded yet.
          </p>
        ) : (
          <ol className="space-y-0 border-l pl-4">
            {history.map((c) => (
              <li key={c.id} className="relative pb-4">
                <span className="absolute -left-[1.32rem] top-1.5 h-2 w-2 rounded-full bg-muted-foreground/40" />
                <div className="flex flex-wrap items-baseline gap-2 text-sm">
                  <span className="font-medium">{describeChange(c)}</span>
                  {c.mif_column && <span className="font-mono text-xs">{c.mif_column}</span>}
                  <SourceBadge source={c.source} status={c.status} />
                </div>
                {(c.old_value || c.new_value) && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    <span className="line-through">{c.old_value || 'nothing'}</span>
                    {' → '}
                    <span className="text-foreground">{c.new_value || 'nothing'}</span>
                  </div>
                )}
                {c.note && <p className="mt-0.5 max-w-2xl text-xs text-muted-foreground">{c.note}</p>}
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {new Date(c.created_at).toLocaleString()}
                  {c.actor_name && ` · ${c.actor_name}`}
                  {c.actor_agent && ` · ${c.actor_agent}`}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
};

const describeAttribute = (attribute: string): string =>
  ({
    write_policy: 'What the loader may do',
    data_owner: 'Who owns the data',
    transform: 'How the value is read',
    rationale: 'The reason recorded',
  })[attribute] || attribute;

const readablePolicy = (value: string | null): string => {
  if (!value) return 'nothing';
  return (POLICY_LABELS as Record<string, string>)[value] || value;
};

const describeChange = (c: RuleChange): string => {
  switch (c.change_type) {
    case 'pack_installed':
      return 'Rules installed from the plan layout';
    case 'pack_approved':
      return 'Rules approved';
    case 'pack_reopened':
      return 'Reopened for editing';
    case 'pack_retired':
      return 'Superseded by a newer layout';
    case 'column_added':
      return 'Column added by the plan';
    case 'column_removed':
      return 'Column dropped by the plan';
    case 'target_changed':
      return c.attribute === 'target_source' ? 'Field location' : 'Field mapping';
    case 'policy_changed':
      return 'What the loader may do';
    case 'owner_changed':
      return 'Who owns the data';
    case 'transform_changed':
      return 'How the value is read';
    case 'rationale_changed':
      return 'Reason updated';
    default:
      return c.change_type;
  }
};

const DriftBadge: React.FC<{ type: string; severity: string }> = ({ type, severity }) => {
  const label =
    type === 'column_added'
      ? 'New column'
      : type === 'column_missing'
        ? 'Stopped arriving'
        : 'Reordered';
  const tone =
    severity === 'blocking'
      ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
      : severity === 'review'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
        : 'bg-muted text-muted-foreground';
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{label}</span>;
};

const SourceBadge: React.FC<{ source: string; status: string }> = ({ source, status }) => {
  if (status === 'proposed') {
    return (
      <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-950 dark:text-blue-300">
        proposed by the agent — needs a decision
      </span>
    );
  }
  if (source === 'install') {
    return <span className="text-xs text-muted-foreground">from the layout</span>;
  }
  if (source === 'agent') {
    return <span className="text-xs text-muted-foreground">agent</span>;
  }
  return null;
};

// ===========================================================================
// The history log — TRUS-366
// ===========================================================================

const HistoryTable: React.FC<{ history: MifBatch[] }> = ({ history }) => {
  if (history.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No files loaded yet. Every load will be listed here with what it changed.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Period</TableHead>
            <TableHead>File</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Added</TableHead>
            <TableHead className="text-right">Updated</TableHead>
            <TableHead className="text-right">Protected</TableHead>
            <TableHead>Loaded by</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((b) => (
            <TableRow key={b.id}>
              <TableCell className="text-sm">{b.reporting_period || '—'}</TableCell>
              <TableCell className="max-w-[16rem] truncate text-xs" title={b.source_filename}>
                {b.source_filename}
              </TableCell>
              <TableCell className="text-xs">{b.plan_name || '—'}</TableCell>
              <TableCell>
                <StatusBadge status={b.status} />
              </TableCell>
              <TableCell className="text-right tabular-nums text-sm">{b.members_created}</TableCell>
              <TableCell className="text-right tabular-nums text-sm">{b.members_updated}</TableCell>
              <TableCell className="text-right tabular-nums text-sm">
                {b.plan_summary?.cells_protected ?? '—'}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {b.committed_by_name || b.created_by_name || '—'}
                {b.rolled_back_at && <div className="text-amber-600 dark:text-amber-500">undone</div>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, [string, string]> = {
    committed: ['Loaded', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'],
    rolled_back: ['Undone', 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'],
    failed: ['Failed', 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'],
    planned: ['Reviewed', 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'],
    verified: ['Checked', 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'],
    uploaded: ['Uploaded', 'bg-muted text-muted-foreground'],
    abandoned: ['Discarded', 'bg-muted text-muted-foreground'],
  };
  const [label, tone] = map[status] || [status, 'bg-muted text-muted-foreground'];
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{label}</span>;
};

export default MifImportWizard;
